import { resolveLatestAmendLien } from './lienAmended'
import { retriveLienFromPractice } from './script'

const AMENDED_TYPES = new Set(['amend', 'amended'])
const ORIGINAL_TYPES = new Set(['lien', 'original'])

function isFilled(value) {
    return value != null && `${value}`.trim() !== ''
}

function isAmendedType(type) {
    return AMENDED_TYPES.has(`${type || ''}`.toLowerCase())
}

function packageFieldForSide(side) {
    return side === 'patient' ? 'patient_package_uuid' : 'practice_package_uuid'
}

/**
 * Format source lien book/page for a county side.
 * @param {object|null|undefined} sourceLien
 * @param {'patient'|'practice'} side
 * @returns {string} e.g. "10 / 20", "10", "20", or ""
 */
export function sourceLienBookPage(sourceLien, side) {
    if (!sourceLien) {
        return ''
    }

    const book = side === 'patient' ? sourceLien.patient_book : sourceLien.practice_book
    const page = side === 'patient' ? sourceLien.patient_page : sourceLien.practice_page
    const bookStr = isFilled(book) ? `${book}`.trim() : ''
    const pageStr = isFilled(page) ? `${page}`.trim() : ''

    if (bookStr && pageStr) {
        return `${bookStr} / ${pageStr}`
    }
    if (bookStr) {
        return bookStr
    }
    if (pageStr) {
        return pageStr
    }
    return ''
}

/**
 * Resolve which lien row satisfies a given county side.
 * @param {{ liens?: object[] }} practice
 * @param {'patient'|'practice'} side
 * @returns {object|null}
 */
export function resolveSatisfactionSourceForSide(practice, side) {
    if (!practice || !Array.isArray(practice.liens)) {
        return null
    }

    const packageField = packageFieldForSide(side)
    const amended = resolveLatestAmendLien(practice.liens)

    if (amended && isFilled(amended[packageField])) {
        return amended
    }

    const original = retriveLienFromPractice(practice.liens, 'lien')
        || practice.liens.find((row) => ORIGINAL_TYPES.has(`${row?.type || ''}`.toLowerCase()))

    if (original && isFilled(original[packageField])) {
        return original
    }

    return null
}

/**
 * Prefer the satisfy row's patient/practice source lien id for the side being shown.
 * Falls back to resolveSatisfactionSourceForSide when the id is missing or not found.
 * @param {{ liens?: object[] }} practice
 * @param {object|null|undefined} satisfyItem
 * @param {'patient'|'practice'} side
 * @returns {object|null}
 */
export function resolveSatisfactionSourceLienForDisplay(practice, satisfyItem, side) {
    const sourceId = side === 'patient'
        ? satisfyItem?.patient_source_lien_id
        : satisfyItem?.practice_source_lien_id

    if (sourceId != null && `${sourceId}`.trim() !== '' && Array.isArray(practice?.liens)) {
        const found = practice.liens.find((row) => Number(row?.id) === Number(sourceId))
        if (found) {
            return found
        }
    }

    return resolveSatisfactionSourceForSide(practice, side)
}

/**
 * @param {{ liens?: object[] }} practice
 * @returns {Array<{ side: 'patient'|'practice', sourceLien: object }>}
 */
export function resolveSatisfactionSidesToFile(practice) {
    return ['patient', 'practice']
        .map((side) => {
            const sourceLien = resolveSatisfactionSourceForSide(practice, side)
            return sourceLien ? { side, sourceLien } : null
        })
        .filter(Boolean)
}

export function satisfactionStageLabel(sourceLien) {
    return isAmendedType(sourceLien?.type) ? 'Amended Lien' : 'Original Lien'
}

export function satisfactionDocString(sourceLien) {
    return isAmendedType(sourceLien?.type) ? 'Amendlien_Satisfaction' : 'Origlien_Satisfaction'
}
